import { Link } from "react-router-dom";
import {
  ArrowTrendingLines20Regular,
  Building20Regular,
  Clock20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import {
  BarRow,
  Banner,
  Button,
  Callout,
  Card,
  DataTable,
  PageHead,
  Panel,
  Skeleton,
  StatCard,
  StatusPill,
  TrendChart,
  type Column,
} from "@guestpilot/ui-kit";
import { useGet, type Schema } from "../api";
import { formatDate, formatMoney, formatNumber, formatPct, monthLabel } from "../format";

type Dash = Schema<"ConsoleDashboard">;
type Customer = Schema<"Customer">;

const PLAN_LABEL: Record<string, string> = {
  starter: "Starter",
  business: "Business",
  scale: "Scale",
  enterprise: "Enterprise",
};

/** Business health at a glance: every number is a live rollup over the same records the other screens list (BR-246, BR-265). */
export function Dashboard() {
  const q = useGet<Dash>("/dashboard");
  if (q.isError)
    return (
      <Banner tone="danger" action={<Button onClick={() => void q.refetch()}>Retry</Button>}>
        Could not load the dashboard.
      </Banner>
    );
  const d = q.data;
  if (!d) return <Skeleton rows={6} />;
  const maxPlan = Math.max(1, ...(d.planMix ?? []).map((p) => p.count ?? 0));
  const signups: Column<Customer>[] = [
    {
      key: "name",
      header: "Customer",
      render: (c) =>
        c.organizationId ? (
          <Link to={`/customers/${c.organizationId}`} className="t-body-strong">
            {c.name}
          </Link>
        ) : (
          <span className="t-body-strong">{c.name}</span>
        ),
    },
    { key: "plan", header: "Plan", render: (c) => (c.planKey ? (PLAN_LABEL[c.planKey] ?? c.planKey) : "—") },
    { key: "at", header: "Signed up", render: (c) => formatDate(c.signedUpAt) },
    {
      key: "status",
      header: "Status",
      render: (c) => (
        <StatusPill
          domain="customer"
          value={c.status ?? "pending"}
          label={STATUS_LABEL[c.status ?? "pending"] ?? ""}
        />
      ),
    },
  ];
  return (
    <div className="gp-page">
      <PageHead title="Dashboard" subtitle="How GuestPilot is doing right now." />
      <div className="gp-grid-4">
        <StatCard
          icon={<ArrowTrendingLines20Regular />}
          label="MRR"
          value={formatMoney(d.mrrCents)}
          caption={`${formatPct(d.mrrDeltaPct ?? 0)} vs last month`}
        />
        <StatCard
          icon={<Building20Regular />}
          label="Active customers"
          value={formatNumber(d.activeCustomers ?? 0)}
          caption={`${formatPct(d.activeDeltaPct ?? 0)} vs last month`}
        />
        <StatCard
          icon={<Clock20Regular />}
          label="Trials in progress"
          value={formatNumber(d.trialsInProgress ?? 0)}
        />
        <StatCard
          icon={<Warning20Regular />}
          label="At risk"
          value={formatNumber(d.atRiskCount ?? 0)}
          caption="Past due or red health"
          attention={(d.atRiskCount ?? 0) > 0}
        />
      </div>
      <div className="gp-grid-2">
        <Card title="MRR trend">
          <TrendChart
            label="MRR over the last 12 months"
            format={(v) => formatMoney(v)}
            points={(d.mrrTrend ?? []).map((p) => ({
              label: monthLabel(p.month ?? ""),
              value: p.mrrCents ?? 0,
            }))}
          />
        </Card>
        <Card title="Customers by plan">
          <div className="gp-stack">
            {(d.planMix ?? []).map((p) => (
              <BarRow
                key={p.planKey}
                label={PLAN_LABEL[p.planKey ?? ""] ?? p.planKey ?? ""}
                value={p.count ?? 0}
                max={maxPlan}
                right={<span className="t-body-sm gp-muted">{Math.round(p.pct ?? 0)}%</span>}
              />
            ))}
          </div>
        </Card>
      </div>
      <section className="gp-stack" aria-labelledby="signups-h">
        <h2 id="signups-h" className="t-title-sm" style={{ margin: 0 }}>
          Recent signups
        </h2>
        <Panel label="Recent signups">
          <DataTable
            caption="Recent signups"
            columns={signups}
            rows={d.recentSignups}
            rowKey={(c) => c.organizationId ?? c.attemptId ?? ""}
            emptyTitle="No signups yet"
          />
        </Panel>
      </section>
      <div className="gp-grid-3">
        <Callout
          tone="warn"
          title={`Trials ending this week: ${formatNumber(d.trialsEndingThisWeek ?? 0)}`}
          action={<Link to="/customers?status=trial">Review</Link>}
        />
        <Callout
          tone="warn"
          title={`Failed payments: ${formatNumber(d.failedPayments?.count ?? 0)} · ${formatMoney(d.failedPayments?.amountCents ?? 0)}`}
          action={<Link to="/billing?status=failed">Review</Link>}
        />
        <Callout
          title={`Enrollment: ${formatNumber(d.enrollmentAttempts ?? 0)} attempts · ${Math.round(d.enrollmentConversionPct ?? 0)}% converted`}
          action={<Link to="/enrollment">Review</Link>}
        />
      </div>
    </div>
  );
}

export const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  trial: "Trial",
  active: "Active",
  past_due: "Past due",
  churned: "Churned",
};
